// Picks the frame to use as an asset's cover/header image from a scan's frame list.
//
// Frames come back from the API in arbitrary order (random row keys), so we sort by the zero-padded
// sequence in the file name first, then take the frame ~30% of the way through the capture. That's a
// representative "into the walk" shot rather than the very first frame, which is often a doorway,
// the operator's feet, or a blurry start.
//
// 360 scans emit one webp per cubemap face (…_front / …_pz / …_left …). For those we keep only the
// FRONT face so the cover looks forward. (This mirrors the front/pz preference in
// lib/viewer/pointCloudCull.js — deliberately duplicated here as a tiny regex so the lightweight
// assets list doesn't pull in the THREE-heavy viewer library.)

const COVER_FRACTION = 0.3;
const ANY_FACE_RE = /[_-](front|back|left|right|up|down|top|bottom|px|nx|py|ny|pz|nz)(\.[^.]+)?$/i;
const FRONT_FACE_RE = /[_-](front|pz)(\.[^.]+)?$/i;

/**
 * @param {Array<{ imagePath?: string, fileName?: string }>} frames
 * @returns {object|null} the chosen frame (with its imagePath), or null if none usable.
 */
export function pickCoverFrame(frames) {
  const list = Array.isArray(frames) ? frames : [];
  const usable = list.filter(f => f && f.imagePath && f.fileName);
  if (!usable.length) {
    // Nothing with a file name to sort/branch on — best-effort first frame that has an image.
    return list.find(f => f && f.imagePath) || null;
  }

  // 360 scan? (any face-suffixed frames) → keep the front face so the cover faces forward.
  const is360 = usable.some(f => ANY_FACE_RE.test(f.fileName));
  const front = is360 ? usable.filter(f => FRONT_FACE_RE.test(f.fileName)) : usable;
  const pool = front.length ? front : usable;

  pool.sort((a, b) => a.fileName.localeCompare(b.fileName, undefined, { numeric: true, sensitivity: 'base' }));

  const index = Math.min(pool.length - 1, Math.floor(COVER_FRACTION * pool.length));
  return pool[index] || pool[0] || null;
}
